import { postRequest } from "./postRequest";

// src/api/adminOrders.ts
export interface ApiResponse<T> {
    success: boolean;
    errorCode?: string | null;
    errorDetail?: string | null;
    data?: T;
  }
  
  // ---- Server DTO eşleştirmeleri ----
  export type Money = number;
  
  export interface ASiparisListeRequest {
    durumKodu?: number;
    odemeDurumu?: number;
    musteriId?: number;
    dateFrom?: string; // ISO
    dateTo?: string;   // ISO (exclusive)
    q?: string;
    page?: number;
    pageSize?: number;
    sort?: "yeni" | "eski";
  }
  
  export interface ASiparisCardDto {
    siparisId: number;
    siparisNo: string;
    musteriId?: number | null;
    durumKodu: number;
    odemeDurumu: number;
    paraBirimiKodu: string;
    araToplamNet: Money;
    indirimToplamNet: Money;
    kargoUcretiNet: Money;
    vergiToplam: Money;
    genelToplamBrut: Money;
    siparisTarihi: string;
    guncellemeTarihi?: string | null;
    musteriAdSoyad?: string | null;
    musteriEposta ?: string | null;
  }
  export interface ASiparisListeResponse {
    items: ASiparisCardDto[];
    total: number;
    page: number;
    pageSize: number;
  }
  
  export interface MSiparisHeaderDto {
    siparisId: number;
    siparisNo: string;
    musteriId?: number | null;
    durumKodu: number;
    paraBirimiKodu: string;
    araToplamNet: Money;
    indirimToplamNet: Money;
    kargoUcretiNet: Money;
    vergiToplam: Money;
    genelToplamBrut: Money;
    faturaAdresiJson?: string | null;
    teslimatAdresiJson?: string | null;
    odemeDurumu: number;
    notlar?: string | null;
    siparisTarihi: string;
    guncellemeTarihi?: string | null;
    musteriAdSoyad?: string | null;
    musteriEposta ?: string | null;
  }
  export interface MSiparisItemDto {
    siparisKalemId: number;
    urunId: number;
    varyantId: number;
    urunAdi: string;
    secenekOzeti?: string | null;
    adet: number;
    birimFiyatNet: Money;
    vergiOraniYuzde: number;
    satirIndirimNet: Money;
    satirVergi: Money;
    satirToplamBrut: Money;
    olusturmaTarihi: string;
  }
  export interface MOdemeDto {
    odemeIslemId: number;
    odemeSaglayici?: string | null;
    islemAnahtari?: string | null;
    tutarBrut: Money;
    paraBirimiKodu: string;
    islemDurumu: number; // 1 tahsil, 2 iade, 3 kısmi iade
    olusturmaTarihi: string;
  }
  export interface MSevkiyatDto {
    sevkiyatId: number;
    kargoSirketAdi?: string | null;
    takipNo?: string | null;
    durumKodu: number;
    notlar?: string | null;
    olusturmaTarihi: string;
    guncellemeTarihi?: string | null;
  }
  
  export interface ASiparisDetayResponse {
    header: MSiparisHeaderDto;
    items: MSiparisItemDto[];
    payments: MOdemeDto[];
    shipments: MSevkiyatDto[];
  }
  
  export interface ASiparisDurumRequest {
    siparisId: number;
    durumKodu: number;
  }
  export interface ASiparisNotRequest {
    siparisId: number;
    notlar?: string | null;
  }
  export interface AOdemeEkleRequest {
    siparisId: number;
    odemeSaglayici?: string | null;
    islemAnahtari?: string | null;
    tutarBrut: Money;
    paraBirimiKodu?: string | null;
    islemDurumu: 1 | 2 | 3;
  }
  export interface ASevkiyatEkleRequest {
    siparisId: number;
    kargoSirketAdi?: string | null;
    takipNo?: string | null;
    durumKodu: number;
    notlar?: string | null;
  }
  export interface ASevkiyatGuncelleRequest extends ASevkiyatEkleRequest {
    sevkiyatId: number;
  }
  export interface ASevkiyatSilRequest {
    sevkiyatId: number;
  }
  export interface ASiparisSilRequest {
    siparisId: number;
  }
  
  // ---- postRequest helper ----
  
  const base = "/admin/siparis";
  
  export const adminOrdersApi = {
    list: (p: ASiparisListeRequest) =>
      postRequest<ASiparisListeRequest, ApiResponse<ASiparisListeResponse>>(
        `${base}/listele`,
        {
          DurumKodu: p.durumKodu,
          OdemeDurumu: p.odemeDurumu,
          MusteriId: p.musteriId,
          DateFrom: p.dateFrom ? new Date(p.dateFrom) : undefined,
          DateTo: p.dateTo ? new Date(p.dateTo) : undefined,
          Q: p.q,
          Page: p.page ?? 1,
          PageSize: p.pageSize ?? 50,
          Sort: p.sort ?? "yeni",
        } as any
      ),
  
    detail: (siparisId: number) =>
      postRequest<{ SiparisId: number }, ApiResponse<ASiparisDetayResponse>>(
        `${base}/detay`,
        { SiparisId: siparisId }
      ),
  
    updateStatus: (p: ASiparisDurumRequest) =>
      postRequest<ASiparisDurumRequest, ApiResponse<string>>(`${base}/durum-guncelle`, {
        SiparisId: p.siparisId,
        DurumKodu: p.durumKodu,
      } as any),
  
    updateNote: (p: ASiparisNotRequest) =>
      postRequest<ASiparisNotRequest, ApiResponse<string>>(`${base}/not-guncelle`, {
        SiparisId: p.siparisId,
        Notlar: p.notlar ?? null,
      } as any),
  
    addPayment: (p: AOdemeEkleRequest) =>
      postRequest<AOdemeEkleRequest, ApiResponse<string>>(`${base}/odeme-ekle`, {
        SiparisId: p.siparisId,
        OdemeSaglayici: p.odemeSaglayici ?? null,
        IslemAnahtari: p.islemAnahtari ?? null,
        TutarBrut: p.tutarBrut,
        ParaBirimiKodu: p.paraBirimiKodu ?? "TRY",
        IslemDurumu: p.islemDurumu,
      } as any),
  
    addShipment: (p: ASevkiyatEkleRequest) =>
      postRequest<ASevkiyatEkleRequest, ApiResponse<number>>(`${base}/sevkiyat-ekle`, {
        SiparisId: p.siparisId,
        KargoSirketAdi: p.kargoSirketAdi ?? null,
        TakipNo: p.takipNo ?? null,
        DurumKodu: p.durumKodu,
        Notlar: p.notlar ?? null,
      } as any),
  
    updateShipment: (p: ASevkiyatGuncelleRequest) =>
      postRequest<ASevkiyatGuncelleRequest, ApiResponse<string>>(`${base}/sevkiyat-guncelle`, {
        SevkiyatId: p.sevkiyatId,
        SiparisId: p.siparisId,
        KargoSirketAdi: p.kargoSirketAdi ?? null,
        TakipNo: p.takipNo ?? null,
        DurumKodu: p.durumKodu,
        Notlar: p.notlar ?? null,
      } as any),
  
    deleteShipment: (sevkiyatId: number) =>
      postRequest<ASevkiyatSilRequest, ApiResponse<string>>(`${base}/sevkiyat-sil`, {
        sevkiyatId: sevkiyatId,
      }),
  
    deleteOrder: (siparisId: number) =>
      postRequest<ASiparisSilRequest, ApiResponse<string>>(`${base}/sil`, {
        siparisId: siparisId,
      }),
  };
  